import { action, computed, observable } from 'mobx'
import { groupBy } from 'lodash-es'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import type { ActivationLifecycle } from '@/infra/activation'
import type { Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { type ArchiveBundleDialogViewModel } from '@/features/admin/bundle/ArchiveBundleDialog/ArchiveBundleDialogViewModel'
import { type BulkApplyDiscountDialogViewModel } from '@/features/admin/bundle/BulkApplyDiscountDialog/BulkApplyDiscountDialogViewModel'
import { type BulkArchiveBundleDialogViewModel } from '@/features/admin/bundle/BulkArchiveBundleDialog/BulkArchiveBundleDialogViewModel'
import { type BulkDuplicateBundleDialogViewModel } from '@/features/admin/bundle/BulkDuplicateBundleDialog/BulkDuplicateBundleDialogViewModel'
import { type BulkEditPriceDialogViewModel } from '@/features/admin/bundle/BulkEditPriceDialog/BulkEditPriceDialogViewModel'
import { type BundleDialogViewModel } from '@/features/admin/bundle/BundleDialog/BundleDialogViewModel'
import { type UnarchiveBundleDialogViewModel } from '@/features/admin/bundle/UnarchiveBundleDialog/UnarchiveBundleDialogViewModel'
import { type Bundle } from './Bundle'
import { BundleItemViewModel } from './BundleItemViewModel'
import { type BundleSearchViewModel } from './BundleSearchViewModel'

/**
 * The dialogs that act on bundles.
 */
export interface BundleDialogs {
  readonly editBundleVm: BundleDialogViewModel
  readonly duplicateBundleVm: BundleDialogViewModel
  readonly archiveBundleVm: ArchiveBundleDialogViewModel
  readonly unarchiveBundleVm: UnarchiveBundleDialogViewModel
  readonly bulkDuplicateBundleVm: BulkDuplicateBundleDialogViewModel
  readonly bulkArchiveBundleVm: BulkArchiveBundleDialogViewModel
  readonly bulkEditPriceVm: BulkEditPriceDialogViewModel
  readonly bulkApplyDiscountVm: BulkApplyDiscountDialogViewModel
}

/**
 * Tracks which bundles are selected for bulk actions.
 */
export interface BundleSelection {
  isSelected(bundleId: Id): boolean
  toggleBundle(checked: boolean, bundleId: Id): void
}

export interface WorkspaceBundleViewModelOptions {
  workspace: Workspace
  searcher: BundleSearchViewModel
  isWorkspaceSelected: () => boolean
  dialogs: BundleDialogs
}

export class WorkspaceBundleViewModel
  implements BundleSelection, ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _bundleVms = new ViewModelList({
    models: () => this.bundles,
    create: (bundle: Bundle) =>
      new BundleItemViewModel({
        bundle,
        selection: this,
        dialogs: this._options.dialogs,
      }),
  })

  @observable
  accessor selectedBundlesId = new Set<Id>()

  constructor(private readonly _options: WorkspaceBundleViewModelOptions) {
    this._lifecycle.manage(this._bundleVms)
  }

  @computed
  get id() {
    return this._options.workspace.id
  }

  @computed
  get workspace() {
    return this._options.workspace
  }

  @computed
  get isFilteredByWorkspace() {
    return this._options.isWorkspaceSelected()
  }

  /**
   * Whether the workspace has bundles.
   */
  @computed
  get hasBundles() {
    return this._options.searcher.allBundles.some(
      (b) => b.workspace.id === this._options.workspace.id,
    )
  }

  /**
   * The bundles in the workspace, ordered by name.
   */
  @computed
  get bundles() {
    return this._options.searcher.allBundles
      .filter((b) => b.workspace.id === this._options.workspace.id)
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  /**
   * The bundle cards in the workspace.
   */
  get bundleVms() {
    return this._bundleVms.items
  }

  /**
   * The bundle cards grouped by whether their bundle contains archived items.
   */
  @computed
  get bundleVmsGroupedByHavingArchivedItems() {
    return groupBy(this.bundleVms, (vm: BundleItemViewModel) =>
      vm.bundle.archivedItemsCount > 0 ? 'hasArchivedItems' : 'allActive',
    )
  }

  @computed
  get showBulkActions() {
    return this.selectedBundlesId.size > 0
  }

  @computed
  get selectedBundles() {
    return this.bundles.filter((bundle) =>
      this.selectedBundlesId.has(bundle.id),
    )
  }

  @computed
  get selectedState() {
    if (this.selectedBundlesId.size === 0) {
      return 'NoneSelected'
    } else if (this.bundles.length === this.selectedBundlesId.size) {
      return 'AllSelected'
    } else {
      return 'SomeSelected'
    }
  }

  isSelected(bundleId: Id) {
    return this.selectedBundlesId.has(bundleId)
  }

  @action.bound
  toggleBundle(checked: boolean, bundleId: Id) {
    if (checked) {
      this.selectedBundlesId.add(bundleId)
    } else {
      this.selectedBundlesId.delete(bundleId)
    }
  }

  @action.bound
  toggleAll() {
    switch (this.selectedState) {
      case 'AllSelected':
        this.selectedBundlesId.clear()
        break
      case 'NoneSelected':
      case 'SomeSelected':
        this.selectedBundlesId = new Set(
          this.bundles.map((bundle) => bundle.id),
        )
        break
    }
  }

  @action.bound
  clearSelectedBundle() {
    this.selectedBundlesId.clear()
  }

  @action.bound
  bulkDuplicate() {
    const selectedBundles = this.selectedBundles

    if (selectedBundles.length === 1) {
      return this._options.dialogs.duplicateBundleVm.openBundle(
        selectedBundles[0],
      )
    }

    this._options.dialogs.bulkDuplicateBundleVm.open(selectedBundles)
  }

  @action.bound
  bulkArchive() {
    void this._options.dialogs.bulkArchiveBundleVm.prompt(this.selectedBundles)
  }

  @action.bound
  bulkEditPrice() {
    this._options.dialogs.bulkEditPriceVm.open(this.selectedBundles)
  }

  @action.bound
  bulkApplyDiscount() {
    this._options.dialogs.bulkApplyDiscountVm.open(this.selectedBundles)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}
